import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  Users, 
  Settings, 
  BarChart3, 
  Plus, 
  Activity, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  FileText,
  Target,
  PieChart,
  ArrowRight
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalEvents: 0,
    activeEvents: 0,
    totalDivisions: 0,
    totalEvaluationItems: 0,
    totalResponses: 0,
    recentEvaluations: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const [eventsRes, divisionsRes, groupsRes, itemsRes] = await Promise.all([
        api.get('/simple.php?action=events'),
        api.get('/simple.php?action=divisions'),
        api.get('/simple.php?action=evaluation-groups'),
        api.get('/simple.php?action=evaluation-items')
      ]);

      const events = eventsRes.data.data;
      const divisions = divisionsRes.data.data;
      const groups = groupsRes.data.data;
      const items = itemsRes.data.data;

      // Calculate active events
      const now = new Date();
      const activeEvents = events.filter(event => {
        if (!event.is_active) return false;
        const endDate = new Date(event.event_dateto);
        return endDate >= now.setHours(0, 0, 0, 0);
      });

      // Get response statistics
      let totalResponses = 0;
      let recentEvaluations = [];
      try {
        const statsRes = await api.get('/simple.php?action=stats');
        if (statsRes.data.success) {
          totalResponses = statsRes.data.totalResponses;
          recentEvaluations = statsRes.data.recentEvaluations;
        }
      } catch (error) {
        console.log('Statistics not available yet');
      }

      setStats({
        totalEvents: events.length,
        activeEvents: activeEvents.length,
        totalDivisions: divisions.length,
        totalEvaluationItems: items.length,
        totalResponses,
        recentEvaluations
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ color: '#6b7280' }}>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Professional Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #3b82f6, #1e40af)',
        borderRadius: '12px',
        padding: '24px 32px',
        marginBottom: '24px',
        color: 'white',
        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)'
      }}>
        <h1 style={{ 
          fontSize: '2rem', 
          margin: 0, 
          marginBottom: '8px',
          fontWeight: '700'
        }}>
          Admin Dashboard
        </h1>
        <p style={{ 
          fontSize: '1rem', 
          margin: 0, 
          opacity: 0.9
        }}>
          Manage your QAME evaluation system
        </p>
      </div>

      {/* Statistics Grid - 4 columns for desktop */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: '20px', 
        marginBottom: '24px' 
      }}>
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '20px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <Calendar size={24} style={{ color: '#3b82f6', marginBottom: '8px' }} />
          <div style={{ fontSize: '2rem', fontWeight: '800', color: '#1f2937', marginBottom: '4px' }}>
            {stats.totalEvents}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '4px' }}>Events</div>
          <div style={{ fontSize: '0.8rem', color: '#10b981' }}>
            {stats.activeEvents} active
          </div>
        </div>

        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '20px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <Users size={24} style={{ color: '#10b981', marginBottom: '8px' }} />
          <div style={{ fontSize: '2rem', fontWeight: '800', color: '#1f2937', marginBottom: '4px' }}>
            {stats.totalResponses}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '4px' }}>Responses</div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
            Submitted evaluations
          </div>
        </div>

        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '20px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <BarChart3 size={24} style={{ color: '#f59e0b', marginBottom: '8px' }} />
          <div style={{ fontSize: '2rem', fontWeight: '800', color: '#1f2937', marginBottom: '4px' }}>
            {stats.totalDivisions}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '4px' }}>Divisions</div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
            Organizational units
          </div>
        </div>

        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '20px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <PieChart size={24} style={{ color: '#8b5cf6', marginBottom: '8px' }} />
          <div style={{ fontSize: '2rem', fontWeight: '800', color: '#1f2937', marginBottom: '4px' }}>
            {stats.totalEvaluationItems}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '4px' }}>Items</div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
            Assessment criteria
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
        {/* Quick Actions - Left Column */}
        <div style={{ 
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <h2 style={{ 
            fontSize: '1.3rem', 
            marginBottom: '20px', 
            color: '#1f2937',
            fontWeight: '600'
          }}>Management Tools</h2>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px'
          }}>
            <Link 
              to="/admin/events" 
              style={{ 
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 16px',
                borderRadius: '8px',
                background: '#3b82f6',
                color: 'white',
                fontSize: '0.9rem',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = '#2563eb'}
              onMouseLeave={(e) => e.target.style.background = '#3b82f6'}
            >
              <Calendar size={16} />
              Manage Events
            </Link>

            <Link 
              to="/admin/divisions" 
              style={{ 
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 16px',
                borderRadius: '8px',
                background: '#10b981',
                color: 'white',
                fontSize: '0.9rem',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = '#059669'}
              onMouseLeave={(e) => e.target.style.background = '#10b981'}
            >
              <Users size={16} />
              Manage Divisions
            </Link>

            <Link 
              to="/admin/evaluations" 
              style={{ 
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 16px',
                borderRadius: '8px',
                background: '#f59e0b',
                color: 'white',
                fontSize: '0.9rem',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = '#d97706'}
              onMouseLeave={(e) => e.target.style.background = '#f59e0b'}
            >
              <Settings size={16} />
              Evaluation Setup
            </Link>

            <Link 
              to="/admin/results" 
              style={{ 
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 16px',
                borderRadius: '8px',
                background: '#8b5cf6',
                color: 'white',
                fontSize: '0.9rem',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = '#7c3aed'}
              onMouseLeave={(e) => e.target.style.background = '#8b5cf6'}
            >
              <BarChart3 size={16} />
              Analytics
            </Link>
          </div>
        </div>

        {/* Recent Activity - Right Column */}
        <div style={{ 
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <h2 style={{ 
            fontSize: '1.3rem', 
            margin: 0, 
            marginBottom: '20px',
            color: '#1f2937',
            fontWeight: '600'
          }}>Recent Activity</h2>
          
          {stats.recentEvaluations && stats.recentEvaluations.length > 0 ? (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {stats.recentEvaluations.slice(0, 8).map((evaluation, index) => (
                <div 
                  key={evaluation.session_id} 
                  style={{ 
                    padding: '12px 0',
                    borderBottom: index < 7 ? '1px solid #f3f4f6' : 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}
                >
                  <div style={{ 
                    fontSize: '0.9rem', 
                    color: '#1f2937', 
                    fontWeight: '500',
                    marginBottom: '2px'
                  }}>
                    {evaluation.event_title}
                  </div>
                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.8rem',
                    color: '#6b7280'
                  }}>
                    <span>{evaluation.division_name}</span>
                    <span>{new Date(evaluation.submitted_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '0.9rem',
              padding: '20px'
            }}>
              <Activity size={24} style={{ opacity: 0.5, marginBottom: '8px' }} />
              <div>No recent activity</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;